<script setup lang="ts">
import { computed } from 'vue'
import UiFieldContainer from './UiFieldContainer.vue'
import UiChip from './UiChip.vue'
import UiIcon from './UiIcon.vue'
import UiMultiSelectCombobox from './UiMultiSelectCombobox.vue'
import type { UiSelectOption } from './UiSelect.vue'
import type { ComboboxGroupData } from './types/combobox'

export interface UiMultiSelectChipsProps {
    name?: string
    id?: string
    options: UiSelectOption[]
    optionGroups?: ComboboxGroupData[]
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    hint?: string
    error?: string
    invalid?: boolean
    max?: number
    openOnClick?: boolean
    showChevron?: boolean
}

const props = withDefaults(defineProps<UiMultiSelectChipsProps>(), {
    size: 'md',
    options: () => [],
    optionGroups: () => [],
    showChevron: false,
})

const model = defineModel<string[]>({ default: [] })

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const valueToLabel = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const opt of props.options) {
        map[opt.value] = opt.label
    }
    return map
})

function remove(value: string) {
    if (props.disabled) return
    model.value = model.value.filter(v => v !== value)
}
</script>

<template>
    <div class="flex flex-col gap-3 w-full">
        <UiMultiSelectCombobox v-model="model" :options="props.options" :placeholder="props.placeholder"
            :optionGroups="props.optionGroups"
            :id="props.id || props.name" :name="props.name" :disabled="props.disabled" :size="props.size"
            :hint="props.hint" :error="props.error" :invalid="props.invalid" :showPlaceholderWhenSelected="true" 
            :max="props.max" :openOnClick="props.openOnClick" :showChevron="props.showChevron" @validate="emit('validate')" />
        <div class="flex flex-wrap items-center gap-2 empty:hidden">
            <UiChip
                v-for="value in model"
                :key="value"
                :text="valueToLabel[value] || value"
                size="sm"
                rounded="full"
                :icon="props.disabled ? undefined : 'close'"
                :class="props.disabled ? 'pointer-events-none opacity-70' : ''"
                @click="remove(value)"
            />
        </div>
    </div>
</template>
